import { useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { ResolvedExperimentData } from '@change-corgi/experiments/configs';

import {
	fetchUkSurgingPetitionToastExperiment,
	fetchUkSurgingPetitionToastPetition,
	UK_SURGING_PETITION_TOAST_EXPERIMENT_QUERY_KEY,
	UK_SURGING_PETITION_TOAST_PETITION_QUERY_KEY,
} from '../../api/api';
import type { UkSurgingPetitionToastPetition } from '../../api/api';
import { FEATURED_PETITION_SLUG } from '../../shared/constants';
import { useQualifyingSurface } from '../useQualifyingSurface';
import { useScrollDepthTrigger } from '../useScrollDepthTrigger';
import { useToastSessionState } from '../useToastSessionState';

import { useDismissState } from './useDismissState';
import { useToastActivation } from './useToastActivation';

type ToastPetition = Readonly<{
	displayTitle: string;
	photo: { url: string; processing: boolean } | null;
	signatureCount: number;
}>;

type Result = Readonly<{
	// Resolved experiment assignment (both arms), for firing exposure. Undefined until resolved.
	experiment: ResolvedExperimentData | undefined;
	shouldRenderToast: boolean;
	petition: ToastPetition | undefined;
	onDismiss: () => void;
	onCtaClick: () => void;
}>;

/**
 * Maps the fetched petition to the presentational shape, live iff the featured petition resolved
 * and is PUBLISHED.
 */
function toLiveToastPetition(petition: UkSurgingPetitionToastPetition | null | undefined): ToastPetition | undefined {
	if (petition == null || petition.status !== 'PUBLISHED') return undefined;

	return {
		displayTitle: petition.displayTitle,
		photo: petition.photo?.petitionSmall ?? null,
		signatureCount: petition.signatureState.signatureCount.exact,
	};
}

/**
 * Orchestrates the toast lifecycle: arm the scroll trigger on a *qualifying surface* while the
 * *session flag* is unset; on the first *eligibility crossing*, consume the flag and fetch the
 * experiment assignment (both arms, so exposure conditions stay arm-independent); for `variation_1`,
 * fetch the featured petition and render the toast when it is live. Route changes and dismiss hide a
 * visible toast; the consumed flag prevents any re-show that session.
 */
export function useUkSurgingPetitionToast(): Result {
	const utilityContext = useUtilityContext();
	const isQualifyingSurface = useQualifyingSurface();
	const { hasBeenShown, markAsShown } = useToastSessionState();

	const armed = isQualifyingSurface && !hasBeenShown;
	const hasCrossedThreshold = useScrollDepthTrigger({ enabled: armed });
	const activated = useToastActivation({ armed, hasCrossedThreshold, markAsShown });
	const { dismissed, onDismiss } = useDismissState();

	const { data: experiment } = useQuery({
		queryKey: [UK_SURGING_PETITION_TOAST_EXPERIMENT_QUERY_KEY],
		queryFn: async () => fetchUkSurgingPetitionToastExperiment(utilityContext),
		enabled: activated,
	});

	const isVariation1 = experiment?.variation === 'variation_1';

	// Control never pays the live Signature Service call — only variation_1 fetches petition data.
	const { data: petitionData } = useQuery({
		queryKey: [UK_SURGING_PETITION_TOAST_PETITION_QUERY_KEY],
		queryFn: async () => fetchUkSurgingPetitionToastPetition(utilityContext, { slug: FEATURED_PETITION_SLUG }),
		enabled: activated && isVariation1,
	});

	const petition = toLiveToastPetition(petitionData);
	const shouldRenderToast = activated && isVariation1 && !dismissed && petition !== undefined;

	return {
		experiment: activated ? experiment : undefined,
		shouldRenderToast,
		petition,
		onDismiss,
		onCtaClick: onDismiss,
	};
}
